import { useState } from 'react';
import { toast } from 'react-toastify';
import Button from 'components/button';
import ModalBody from 'components/modal/components/body';
import ModalFooter from 'components/modal/components/footer';
import Modalheader from 'components/modal/components/header';
import useModal from 'components/modal/useModal';
import JustificatifConfirmation, { CONFIRMATION_TITLE } from 'components/justificatifConfirmation/JustificatifConfirmation';
import useJustificatifConfirmation from 'hooks/useJustificatifConfirmation';
import UseContext from 'hooks/useContext';

export const REMUNERATION_CHANGES_MODAL = 'remuneration_changes';

/**
 * The one confirmation of the remuneration changes held back on the page
 * (JBWEB-1468): each one, before and after, then a single justificatif for
 * all of them. `modalData`: `changes`, and `onConfirm(justificatif)`, which
 * saves them and resolves with the keys refused.
 */
const ModalRemunerationChanges = () => {
  const { toggle } = useModal();
  const { modalData } = UseContext();
  const justificatif = useJustificatifConfirmation();
  const [isSaving, setIsSaving] = useState(false);
  const changes = modalData?.changes ?? [];
  const isBusy = isSaving || justificatif.isUploading;

  const save = async (storedJustificatif) => {
    setIsSaving(true);
    const failed = await modalData.onConfirm(storedJustificatif);
    setIsSaving(false);
    if (failed.length) {
      toast.error(
        failed.length === changes.length
          ? 'Aucune modification n’a été enregistrée'
          : `${changes.length - failed.length} modification(s) enregistrée(s), ${failed.length} refusée(s)`,
      );
    } else {
      toast.success(changes.length > 1 ? 'Les modifications ont été enregistrées' : 'La modification a été enregistrée');
    }
    toggle();
  };

  return (
    <>
      <Modalheader
        hide={toggle}
        hideShow={true}
        description={`Ces paramètres ont un impact direct sur les contrats et les factures. ${
          changes.length > 1 ? `Un seul justificatif couvre les ${changes.length} modifications.` : 'Un justificatif couvre cette modification.'
        }`}
      >
        {CONFIRMATION_TITLE}
      </Modalheader>
      <ModalBody>
        <div className="flex flex-col gap-4 text-sm">
          <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
            {changes.map((change) => (
              <li key={change.key} className="flex items-start justify-between gap-4 px-3 py-2.5">
                <span className="text-gray-600">{change.label}</span>
                <span className="text-right">
                  {change.before && <span className="text-gray-400 line-through">{change.before}</span>}
                  {change.before && <span className="px-1.5 text-gray-400">→</span>}
                  <span className="font-medium text-gray-900">{change.after}</span>
                </span>
              </li>
            ))}
          </ul>
          <JustificatifConfirmation
            file={justificatif.file}
            setFile={justificatif.setFile}
            isAttested={justificatif.isAttested}
            setIsAttested={justificatif.setIsAttested}
            error={justificatif.error}
            description={null}
          />
        </div>
      </ModalBody>
      <ModalFooter>
        <Button size="LG" color="SECONDARY" onClick={toggle}>
          Retour
        </Button>
        <Button
          size="LG"
          color={isBusy || !justificatif.canConfirm ? 'DISABLED' : 'PRIMARY'}
          onClick={isBusy ? undefined : () => justificatif.confirm(save)}
        >
          {isBusy ? 'Enregistrement ...' : 'Confirmer'}
        </Button>
      </ModalFooter>
    </>
  );
};

export default ModalRemunerationChanges;
